import React, { useEffect, useMemo } from 'react';
import { ERROR_TYPE } from '@soundxyz/utils/constants/logging';
import { LandingHeading } from '@components/Landing/shared/LandingHeading';
import { UpcomingDropCard, UpcomingDropSkeletonCard } from '@components/SongCards/UpcomingDropCard';
import { GridCarousel } from '@components/main-components/carousel/GridCarousel';
import { Chip } from '@components/main-components/elements/chips/Chip';
import { useAuth } from '@context/auth/base';
import { triggerLogin } from '@context/auth/store';
import type { ReleasesCursorFilterArgs } from '@graphql/reactQuery';
import { gql, UpcomingDropsCarouselDocument, useInfiniteQuery } from '@graphql/reactQuery';
import { updateUpcomingPageStatus } from '@hooks/ReleaseStatus/useReleaseStatus';
import { logError } from '@hooks/useLogErrorNew';
import { BASE_UPCOMING_FILTER } from '@hooks/useUpcomingDrops';
import {
  CLIENT_QUERY_REDUCTION_ON_FRESH_SSR_STRATEGY,
  GRID_CAROUSEL_INITIAL_PAGE,
  NAV_LINKS,
} from '@utils/constants';
import { getNextTrackIds } from '@utils/helpers';
import { EmptyState } from '../FeaturedStatus/EmptyState';
import { FeaturedStatusEnum } from '../FeaturedStatus/schema';
import { useFeaturedStatus } from '../FeaturedStatus/useFeaturedStatus';

/*#__PURE__*/ gql(/* GraphQL */ `
  query UpcomingDropsCarousel(
    $filter: ReleasesCursorFilterArgs
    $pagination: ReleasesCursorConnectionArgs!
  ) {
    releases(pagination: $pagination, filter: $filter) {
      edges {
        node {
          id
          mintStartTime
          track {
            id
          }
          ...UpcomingDropCard
        }
        cursor
      }
      pageInfo {
        startCursor
        endCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
`);

export const UpcomingDropsSectionV2 = ({
  heading,
  columnAmount,
}: {
  heading: string;
  columnAmount?: number;
}) => {
  const { isAuthenticatedWithAPI, authUser } = useAuth();
  const { upcoming, updateUpcoming } = useFeaturedStatus();
  const [followingLoading, setFollowingLoading] = React.useState(false);

  useEffect(() => {
    if (isAuthenticatedWithAPI) {
      updateUpcoming(FeaturedStatusEnum.Enum.FOLLOWING);
      setFollowingLoading(true);
    }
  }, [isAuthenticatedWithAPI, updateUpcoming]);

  const LOADING_SLIDES = useMemo(() => {
    return Array.from({ length: columnAmount ?? 5 }).map((_, i) => ({
      id: `loading-skeleton-card-${i}`,
      slide: <UpcomingDropSkeletonCard />,
    }));
  }, [columnAmount]);

  const filter: ReleasesCursorFilterArgs | undefined = useMemo(() => {
    const artistFollowedByUser =
      upcoming === FeaturedStatusEnum.Enum.FOLLOWING && authUser ? authUser.id : undefined;
    return {
      ...BASE_UPCOMING_FILTER,
      artistFollowedByUser,
      featuredStatus: upcoming === FeaturedStatusEnum.Enum.HOT ? upcoming : 'ALL',
    };
  }, [authUser, upcoming]);

  const { isLoading, orderedList, loadMoreNextPage } = useInfiniteQuery(
    UpcomingDropsCarouselDocument,
    {
      enabled: Boolean(filter),
      filterQueryKey: {
        filter,
      },
      variables({ pageParam }) {
        return {
          filter,
          pagination: {
            first: GRID_CAROUSEL_INITIAL_PAGE,
            after: pageParam?.after || null,
            sort: {
              mintStartTime: 'ASC' as const,
            },
          },
        };
      },
      getNextPageParam({
        data: {
          releases: { pageInfo },
        },
      }) {
        return pageInfo.hasNextPage && { after: pageInfo.endCursor };
      },
      list({ releases }) {
        return releases.edges.map(edge => edge.node);
      },
      uniq({ id }) {
        return id;
      },
      order: [
        [v => v.mintStartTime, v => v.id],
        ['asc', 'asc'],
      ],
      onError(error) {
        logError({
          feature: 'LANDING_PAGE',
          error,
          errorType: ERROR_TYPE.FETCH_GQL_ERROR,
          isArtistOnlyPage: false,
          message: 'Error loading upcoming drops on landing page',
          action: ERROR_TYPE.FETCH_GQL_ERROR,
          level: 'fatal',
        });
      },
      ...CLIENT_QUERY_REDUCTION_ON_FRESH_SSR_STRATEGY,
    },
  );

  const isEmptyList = orderedList.length === 0;

  useEffect(() => {
    if (followingLoading && !isLoading) {
      setFollowingLoading(false);
      if (upcoming === FeaturedStatusEnum.Enum.FOLLOWING && isEmptyList) {
        updateUpcoming(FeaturedStatusEnum.Enum.HOT);
      }
    }
  }, [followingLoading, isLoading, isEmptyList, upcoming, updateUpcoming]);

  const slides = React.useMemo(() => {
    if (isLoading) return LOADING_SLIDES;

    return (
      orderedList.map(release => ({
        id: release.id,
        slide: (
          <UpcomingDropCard
            data={release}
            getNextTrackIds={getNextTrackIds({ trackId: release.track.id, releases: orderedList })}
          />
        ),
      })) || []
    );
  }, [isLoading, LOADING_SLIDES, orderedList]);

  return (
    <div className="w-full px-4 md:px-6">
      <LandingHeading
        header={heading}
        pathnameUri={NAV_LINKS.Upcoming}
        onHeaderClick={() => {
          updateUpcomingPageStatus({
            featuredStatus: 'FOLLOWING',
          });
        }}
      />
      <div className="mb-2 flex items-center gap-2 sm:mb-4">
        <Chip
          text="Hot"
          isSelected={upcoming === FeaturedStatusEnum.Enum.HOT}
          onClick={() => updateUpcoming(FeaturedStatusEnum.Enum.HOT)}
        />
        <Chip
          text="Following"
          isSelected={upcoming === FeaturedStatusEnum.Enum.FOLLOWING}
          onClick={() => {
            if (!isAuthenticatedWithAPI) {
              triggerLogin();
              return;
            }

            updateUpcoming(FeaturedStatusEnum.Enum.FOLLOWING);
          }}
        />
      </div>
      {orderedList.length === 0 && !isLoading ? (
        <EmptyState type="upcoming" status="FOLLOWING" />
      ) : (
        <GridCarousel
          slides={slides}
          onEndReached={loadMoreNextPage}
          onInternalActiveIndexChange={null}
          columnAmount={columnAmount ?? null}
          hideArrows={false}
          externalActiveIndex={null}
          spacing={null}
          alignSlidesLeft={false}
          ssrSlideWidth={undefined}
          columnType="default"
        />
      )}
    </div>
  );
};
